<template>
  <div class="control-panel">
    <!-- 控制面板 -->
    <div class="control-content">
      <div class="panel-header">
        <h3>控制面板</h3>
        <button @click="togglePanel" class="toggle-btn">
          {{ isExpanded ? '收起' : '展开' }}
        </button>
      </div>
      
      <div v-show="isExpanded" class="panel-content">
        <!-- 白天黑夜切换 -->
        <div class="control-group">
          <label class="control-label">场景模式</label>
          <div class="switch-container">
            <span :class="{ active: !$store.getters.isDayMode }">夜晚</span>
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="$store.getters.isDayMode"
                @change="$store.dispatch('updateIsDayMode', $event.target.checked)"
              >
              <span class="slider"></span>
            </label>
            <span :class="{ active: $store.getters.isDayMode }">白天</span>
          </div>
        </div>

        <!-- 梯子起落 -->
        <div class="control-group">
          <label class="control-label">梯子状态</label>
          <div class="switch-container">
            <span :class="{ active: !$store.getters.ladderUp }">落下</span>
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="$store.getters.ladderUp"
                @change="$store.dispatch('updateLadderUp', $event.target.checked)"
              >
              <span class="slider"></span>
            </label>
            <span :class="{ active: $store.getters.ladderUp }">升起</span>
          </div>
        </div>

        <!-- 平台回转角度 -->
        <div class="control-group">
          <label class="control-label">平台回转角度</label>
          <div class="slider-container">
            <input 
              type="range" 
              min="0" 
              max="360" 
              step="1"
              :value="$store.getters.platformRotation"
              @input="$store.dispatch('updatePlatformRotation', Number($event.target.value))"
              class="range-slider"
            >
            <div class="value-display">{{ $store.getters.platformRotation }}°</div>
          </div>
        </div>

        <!-- 推压斗杆伸出长度 -->
        <div class="control-group">
          <label class="control-label">推压斗杆伸出长度</label>
          <div class="slider-container">
            <input 
              type="range" 
              min="0" 
              max="8" 
              step="0.1"
              :value="$store.getters.armExtension"
              @input="$store.dispatch('updateArmExtension', Number($event.target.value))"
              class="range-slider"
            >
            <div class="value-display">{{ $store.getters.armExtension }}米</div>
          </div>
        </div>

        <!-- 提升下放长度 -->
        <div class="control-group">
          <label class="control-label">提升下放长度</label>
          <div class="slider-container">
            <input 
              type="range" 
              min="0" 
              max="13.5" 
              step="0.1"
              :value="$store.getters.liftHeight"
              @input="$store.dispatch('updateLiftHeight', Number($event.target.value))"
              class="range-slider"
            >
            <div class="value-display">{{ $store.getters.liftHeight }}米</div>
          </div>
        </div>

        <!-- 播放铲斗动画 -->
        <div class="control-group">
          <label class="control-label">铲斗动画控制（仅做演示用，会和数据驱动冲突。通过数据驱动时需要初始化。）</label>
          <div class="animation-controls">
            <button 
              @click="toggleAnimation" 
              :class="['animation-btn', getAnimationButtonClass()]"
            >
              {{ getAnimationButtonText() }}
            </button>
            <button 
              v-if="$store.getters.bone007AnimationPlaying"
              @click="stopAnimation" 
              class="animation-btn stop-btn"
            >
              停止
            </button>
          </div>
        </div>



        <!-- 自动恢复视角 -->
        <div class="control-group">
          <label class="control-label">15秒无操作恢复视角</label>
          <div class="switch-container">
            <span :class="{ active: !$store.getters.autoResetCamera }">关闭</span>
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="$store.getters.autoResetCamera"
                @change="$store.dispatch('updateAutoResetCamera', $event.target.checked)"
              >
              <span class="slider"></span>
            </label>
            <span :class="{ active: $store.getters.autoResetCamera }">开启</span>
          </div>
        </div>

        <!-- 天空和地面颜色设置 -->
        <div class="control-group">
          <label class="control-label section-title">天空和地面颜色设置</label>
        </div>

        <!-- 白天模式颜色 -->
        <div class="control-group">
          <label class="control-label">白天模式天空颜色</label>
          
          <div class="color-group">
            <label class="color-label">天空顶部颜色</label>
            <input 
              type="color" 
              :value="$store.getters.dayModeColors.skyTop"
              @input="updateDayColor('skyTop', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">天空中部颜色</label>
            <input 
              type="color" 
              :value="$store.getters.dayModeColors.skyMiddle"
              @input="updateDayColor('skyMiddle', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">天空底部颜色（地平线）</label>
            <input 
              type="color" 
              :value="$store.getters.dayModeColors.skyBottom"
              @input="updateDayColor('skyBottom', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">地面材质颜色</label>
            <input 
              type="color" 
              :value="$store.getters.dayModeColors.groundColor"
              @input="updateDayColor('groundColor', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="slider-container">
            <label class="control-label">地平线渐变范围</label>
            <input 
              type="range" 
              min="0.1" 
              max="0.8" 
              step="0.05"
              :value="$store.getters.dayModeColors.horizonRange"
              @input="updateDayColor('horizonRange', Number($event.target.value))"
              class="range-slider"
            >
            <div class="value-display">{{ $store.getters.dayModeColors.horizonRange.toFixed(2) }}</div>
          </div>
        </div>

        <!-- 夜间模式颜色 -->
        <div class="control-group">
          <label class="control-label">夜间模式天空颜色</label>
          
          <div class="color-group">
            <label class="color-label">天空顶部颜色</label>
            <input 
              type="color" 
              :value="$store.getters.nightModeColors.skyTop"
              @input="updateNightColor('skyTop', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">天空中部颜色</label>
            <input 
              type="color" 
              :value="$store.getters.nightModeColors.skyMiddle"
              @input="updateNightColor('skyMiddle', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">天空底部颜色（地平线）</label>
            <input 
              type="color" 
              :value="$store.getters.nightModeColors.skyBottom"
              @input="updateNightColor('skyBottom', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="color-group">
            <label class="color-label">地面材质颜色</label>
            <input 
              type="color" 
              :value="$store.getters.nightModeColors.groundColor"
              @input="updateNightColor('groundColor', $event.target.value)"
              class="color-picker"
            >
          </div>
          
          <div class="slider-container">
            <label class="control-label">地平线渐变范围</label>
            <input 
              type="range" 
              min="0.1" 
              max="0.8" 
              step="0.05"
              :value="$store.getters.nightModeColors.horizonRange"
              @input="updateNightColor('horizonRange', Number($event.target.value))"
              class="range-slider"
            >
            <div class="value-display">{{ $store.getters.nightModeColors.horizonRange.toFixed(2) }}</div>
          </div>
        </div>


        <!-- AO效果 -->
        <div class="control-group">
          <label class="control-label">GTAO环境光遮蔽</label>
          <div class="switch-container">
            <span :class="{ active: !$store.getters.enableAO }">关闭</span>
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="$store.getters.enableAO"
                @change="$store.dispatch('updateEnableAO', $event.target.checked)"
              >
              <span class="slider"></span>
            </label>
            <span :class="{ active: $store.getters.enableAO }">开启</span>
          </div>
        </div>

        <!-- GTAO参数调整 -->
        <div v-show="$store.getters.enableAO" class="gtao-params">
          <!-- 输出类型 -->
          <div class="control-group">
            <label class="control-label">output</label>
            <div class="select-container">
              <select 
                :value="$store.getters.gtaoOutput"
                @change="$store.dispatch('updateGtaoOutput', $event.target.value)"
                class="select-dropdown"
              >
                <option value="Default">Default</option>
                <option value="Diffuse">Diffuse</option>
                <option value="AO Only">AO Only</option>
                <option value="AO Only + Denoise">AO Only + Denoise</option>
                <option value="Depth">Depth</option>
                <option value="Normal">Normal</option>
              </select>
            </div>
          </div>

          <!-- 混合强度 -->
          <div class="control-group">
            <label class="control-label">blendIntensity</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.01"
                :value="$store.getters.blendIntensity"
                @input="$store.dispatch('updateBlendIntensity', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.blendIntensity.toFixed(2) }}</div>
            </div>
          </div>

          <!-- AO参数标题 -->
          <div class="control-group">
            <label class="control-label section-title">AO Parameters</label>
          </div>

          <!-- radius -->
          <div class="control-group">
            <label class="control-label">radius</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="0.01" 
                max="1" 
                step="0.01"
                :value="$store.getters.aoRadius"
                @input="$store.dispatch('updateAoRadius', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.aoRadius.toFixed(2) }}</div>
            </div>
          </div>

          <!-- distanceExponent -->
          <div class="control-group">
            <label class="control-label">distanceExponent</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="1" 
                max="4" 
                step="0.01"
                :value="$store.getters.distanceExponent"
                @input="$store.dispatch('updateDistanceExponent', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.distanceExponent.toFixed(2) }}</div>
            </div>
          </div>

          <!-- thickness -->
          <div class="control-group">
            <label class="control-label">thickness</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="0.01" 
                max="10" 
                step="0.01"
                :value="$store.getters.thickness"
                @input="$store.dispatch('updateThickness', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.thickness.toFixed(2) }}</div>
            </div>
          </div>

          <!-- distanceFallOff -->
          <div class="control-group">
            <label class="control-label">distanceFallOff</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.01"
                :value="$store.getters.distanceFallOff"
                @input="$store.dispatch('updateDistanceFallOff', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.distanceFallOff.toFixed(2) }}</div>
            </div>
          </div>

          <!-- scale -->
          <div class="control-group">
            <label class="control-label">scale</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="0.01" 
                max="2" 
                step="0.01"
                :value="$store.getters.scale"
                @input="$store.dispatch('updateScale', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.scale.toFixed(2) }}</div>
            </div>
          </div>

          <!-- samples -->
          <div class="control-group">
            <label class="control-label">samples</label>
            <div class="slider-container">
              <input 
                type="range" 
                min="2" 
                max="32" 
                step="1"
                :value="$store.getters.aoSamples"
                @input="$store.dispatch('updateAoSamples', Number($event.target.value))"
                class="range-slider"
              >
              <div class="value-display">{{ $store.getters.aoSamples }}</div>
            </div>
          </div>
        </div>

        <!-- 重置按钮 -->
        <div class="control-group">
          <button @click="resetAll" class="reset-btn">
            重置所有参数
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ControlPanel',
  data() {
    return {
      isExpanded: false // 默认收起状态
    }
  },
  methods: {
    togglePanel() {
      this.isExpanded = !this.isExpanded;
    },

    resetAll() {
      this.$store.dispatch('resetAllParameters');
    },

    // 动画控制方法
    toggleAnimation() {
      if (!this.$store.getters.bone007AnimationPlaying) {
        // 开始播放
        this.$store.dispatch('updateBone007AnimationPlaying', true);
        this.$store.dispatch('updateBone007AnimationPaused', false);
      } else if (this.$store.getters.bone007AnimationPaused) {
        // 恢复播放
        this.$store.dispatch('updateBone007AnimationPaused', false);
      } else {
        // 暂停
        this.$store.dispatch('updateBone007AnimationPaused', true);
      }
    },

    stopAnimation() {
      this.$store.dispatch('updateBone007AnimationPlaying', false);
      this.$store.dispatch('updateBone007AnimationPaused', false);
    },

    getAnimationButtonText() {
      if (!this.$store.getters.bone007AnimationPlaying) {
        return '播放';
      } else if (this.$store.getters.bone007AnimationPaused) {
        return '继续';
      } else {
        return '暂停';
      }
    },

    getAnimationButtonClass() {
      if (!this.$store.getters.bone007AnimationPlaying) {
        return 'play-btn';
      } else if (this.$store.getters.bone007AnimationPaused) {
        return 'resume-btn';
      } else {
        return 'pause-btn';
      }
    },

    // 颜色更新方法
    updateDayColor(colorType, value) {
      const currentColors = { ...this.$store.getters.dayModeColors };
      currentColors[colorType] = value;
      this.$store.dispatch('updateDayModeColors', currentColors);
    },

    updateNightColor(colorType, value) {
      const currentColors = { ...this.$store.getters.nightModeColors };
      currentColors[colorType] = value;
      this.$store.dispatch('updateNightModeColors', currentColors);
    }
  }
}
</script>

<style scoped>
.control-panel {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 320px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* 控制面板样式 */
.control-content {
  width: 100%;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.panel-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.toggle-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.toggle-btn:hover {
  background: #0056b3;
}

.panel-content {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.control-group {
  margin-bottom: 24px;
}

.control-label {
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-weight: 500;
  font-size: 14px;
}

.section-title {
  color: #007bff;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #007bff;
  padding-bottom: 4px;
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}

.switch-container span {
  font-size: 14px;
  color: #666;
  transition: color 0.3s;
}

.switch-container span.active {
  color: #007bff;
  font-weight: 600;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
}

input:checked + .slider {
  background-color: #007bff;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.range-slider {
  flex: 1;
  height: 6px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  background: #007bff;
  cursor: pointer;
}

.range-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: #007bff;
  cursor: pointer;
  border: none;
}

.value-display {
  min-width: 60px;
  text-align: right;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  background: #f8f9fa;
  padding: 4px 8px;
}

.reset-btn {
  width: 100%;
  background: #dc3545;
  color: white;
  border: none;
  padding: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s;
}

.reset-btn:hover {
  background: #c82333;
}



/* 滚动条样式 */
.panel-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.panel-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
}

.panel-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* GTAO参数区域样式 */
.gtao-params {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  margin-top: 12px;
}

.gtao-params .control-group {
  margin-bottom: 16px;
}

.gtao-params .control-group:last-child {
  margin-bottom: 0;
}

.gtao-params .control-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.gtao-params .value-display {
  background: #fff;
  border: 1px solid #dee2e6;
  font-size: 13px;
}

.select-container {
  position: relative;
  width: 100%;
}

.select-dropdown {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-down'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px;
}

.select-dropdown:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* 动画控制按钮样式 */
.animation-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.animation-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  min-width: 60px;
}

.play-btn {
  background: #28a745;
  color: white;
}

.play-btn:hover {
  background: #218838;
}

.pause-btn {
  background: #ffc107;
  color: #212529;
}

.pause-btn:hover {
  background: #e0a800;
}

.resume-btn {
  background: #17a2b8;
  color: white;
}

.resume-btn:hover {
  background: #138496;
}

.stop-btn {
  background: #dc3545;
  color: white;
}

.stop-btn:hover {
  background: #c82333;
}

/* 颜色选择器样式 */
.color-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.color-label {
  font-size: 13px;
  color: #666;
  margin: 0;
  flex: 1;
}

.color-picker {
  width: 50px;
  height: 30px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  background: none;
  padding: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 3px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .control-panel {
    width: calc(100vw - 40px);
    right: 20px;
    left: 20px;
  }
}
</style>